/**
 * Control Centre entry page: five "needs attention today" cards.
 *
 * Everything here is pure (no fetching, no React) so the exception rules, the
 * links and the card order can be tested on their own. Rules this file keeps:
 *   - TODAY only. No yesterday, no 7-day strips, no targets.
 *   - A figure we do not have is Unknown. It is never turned into 0, 100% or
 *     "Nothing needs attention" (Constitution §13.2: unknown stays visible).
 *   - A line is an exception only if someone has to act on it today.
 *   - Each exception line carries the tab and filter it opens, using the same
 *     onNavigate(section, kpi) contract the old alerts used.
 *
 * TODO: switch the time formatting to utils/ukTime once PR #977 is merged.
 */

import type { AquariusData, DriverDetail } from '../types/fleetDataHub';
import type { OrdersDailyRow, OrdersDailySummary } from '../types/ordersDailyTruth';
import type { MaintenanceSummaryData } from '../components/fleetDataHub/MaintenanceSection';
import type { MaintenanceScheduleData } from '../components/fleetDataHub/MaintenanceScheduleView';
import type { FreshnessRow } from './dataFreshness';
import { formatUkDate, formatUkDateTime, formatUkTime, ukClockParts } from '../lib/calc/clock';
import { formatDriverDisplayName } from './formatDriverDisplayName';
import { applyViolationsToStatus } from './driverComplianceRules';
import { isPlaceholderOutstandingDefectRow } from './defectRules';
import { formatPct } from './ordersDailyPresentation';

// ── The UK day ──────────────────────────────────────────────────────────────

/**
 * The UK calendar day `ymd` as two real instants: from London midnight to the
 * last millisecond before the next London midnight. In summer that is
 * 23:00Z to 22:59:59.999Z, not 00:00Z to 23:59Z, which misses the first UK
 * hour and takes in the first hour of tomorrow.
 * TODO: switch to the helper in utils/ukTime once PR #977 is merged.
 */
export function londonDayWindow(ymd: string): { from: string; to: string } {
  const startOf = (day: string): number => {
    const [y, m, d] = day.split('-').map(Number);
    const guess = Date.UTC(y, m - 1, d, 0, 0, 0);
    const p = ukClockParts(new Date(guess));
    if (!p) return guess;
    const asUtc = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
    return guess - (asUtc - guess);
  };
  const [y, m, d] = ymd.split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1)).toISOString().slice(0, 10);
  return { from: new Date(startOf(ymd)).toISOString(), to: new Date(startOf(next) - 1).toISOString() };
}

// ── Cards, links ────────────────────────────────────────────────────────────

export const CARD_IDS = ['orders', 'drivers', 'compliance', 'maintenance', 'telematics'] as const;
export type CardId = (typeof CARD_IDS)[number];

/** Default order, left as the order of CARD_IDS. */
export const DEFAULT_CARD_ORDER: readonly CardId[] = CARD_IDS;

export const CARD_TITLES: Record<CardId, string> = {
  orders: 'Orders',
  drivers: 'Drivers',
  compliance: 'Compliance',
  maintenance: 'Maintenance',
  telematics: 'Telematics',
};

export type NavSection = 'fuel' | 'orders' | 'drivers' | 'gps' | 'maintenance';

/** Where a click goes: the arguments of FleetHubOverview's onNavigate(section, kpi). */
export interface CardTarget {
  section: NavSection;
  kpi?: string;
}

/**
 * Link targets. Each `kpi` is a highlightKPI value the destination tab already
 * handles (checked against the tab code; see the report for what each one does):
 *   orders       'deliveries' / 'collections'  -> OrdersDailyKpiSection filter
 *   drivers      'walkaround'                  -> scrolls to the walkaround table
 *                'violations' / 'at-risk'      -> DriverCompliance modals
 *                'all-drivers'                 -> "All Drivers" modal (fallback branch)
 *   maintenance  'vor' / 'open-defects'        -> MaintenanceSection modals
 *   gps          'vehicle-status'              -> scrolls to the vehicle list
 *                'prolonged-idle'              -> idling filter modal
 */
export const CARD_LINKS = {
  orders: { section: 'orders' },
  ordersDeliveries: { section: 'orders', kpi: 'deliveries' },
  ordersCollections: { section: 'orders', kpi: 'collections' },
  drivers: { section: 'drivers', kpi: 'walkaround' },
  compliance: { section: 'drivers', kpi: 'violations' },
  complianceAtRisk: { section: 'drivers', kpi: 'at-risk' },
  complianceAllDrivers: { section: 'drivers', kpi: 'all-drivers' },
  maintenanceVor: { section: 'maintenance', kpi: 'vor' },
  maintenanceDefects: { section: 'maintenance', kpi: 'open-defects' },
  maintenance: { section: 'maintenance' },
  telematics: { section: 'gps', kpi: 'vehicle-status' },
  telematicsIdle: { section: 'gps', kpi: 'prolonged-idle' },
} as const satisfies Record<string, CardTarget>;

// ── Card order (per browser) ────────────────────────────────────────────────

export const CARD_ORDER_STORAGE_KEY = 'zeefleet.controlCentre.cardOrder.v1';

/**
 * A stored order is used only if it is exactly the five known ids, each once.
 * Anything else (unknown id, missing id, duplicate, not an array) is ignored
 * as a whole and the default order is used.
 */
export function normaliseCardOrder(value: unknown): CardId[] {
  if (!Array.isArray(value) || value.length !== CARD_IDS.length) return [...DEFAULT_CARD_ORDER];
  const seen = new Set<string>();
  for (const v of value) {
    if (typeof v !== 'string' || !(CARD_IDS as readonly string[]).includes(v) || seen.has(v)) {
      return [...DEFAULT_CARD_ORDER];
    }
    seen.add(v);
  }
  return value as CardId[];
}

/** Read the saved order. Blocked storage or corrupt JSON give the default order. */
export function loadCardOrder(): CardId[] {
  try {
    const raw = window.localStorage.getItem(CARD_ORDER_STORAGE_KEY);
    if (!raw) return [...DEFAULT_CARD_ORDER];
    return normaliseCardOrder(JSON.parse(raw));
  } catch {
    return [...DEFAULT_CARD_ORDER];
  }
}

/** Save the order. Returns false when storage is blocked (the order still applies for this visit). */
export function saveCardOrder(order: readonly CardId[]): boolean {
  try {
    window.localStorage.setItem(CARD_ORDER_STORAGE_KEY, JSON.stringify(order));
    return true;
  } catch {
    return false;
  }
}

export function clearCardOrder(): boolean {
  try {
    window.localStorage.removeItem(CARD_ORDER_STORAGE_KEY);
    return true;
  } catch {
    return false;
  }
}

/** Move one card a place up or down. At either end the order is returned unchanged. */
export function moveCard(order: readonly CardId[], id: CardId, direction: 'up' | 'down'): CardId[] {
  const from = order.indexOf(id);
  const to = direction === 'up' ? from - 1 : from + 1;
  if (from < 0 || to < 0 || to >= order.length) return [...order];
  const next = [...order];
  [next[from], next[to]] = [next[to], next[from]];
  return next;
}

// ── Model shared by every card ──────────────────────────────────────────────

/** A figure that is either known or Unknown. `loading` is "not here yet", never a zero. */
export type Figure =
  | { state: 'known'; value: number }
  | { state: 'unknown' }
  | { state: 'loading' };

export const known = (value: number): Figure => ({ state: 'known', value });
export const UNKNOWN: Figure = { state: 'unknown' };
export const LOADING: Figure = { state: 'loading' };

/** One thing that needs someone to act today. */
export interface ExceptionLine {
  id: string;
  text: string;
  /** Null when the count is Unknown. */
  count: number | null;
  target: CardTarget;
  /** `unknown`: could not be worked out; shown plainly, never as "fine". */
  tone: 'attention' | 'unknown';
}

export interface CardModel {
  id: Exclude<CardId, 'orders' | 'compliance'>;
  headline: Figure;
  headlineLabel: string;
  headlineTarget: CardTarget;
  lines: ExceptionLine[];
  /** True only when every figure is known and none needs action. */
  nothingNeedsAttention: boolean;
}

export const NOTHING_NEEDS_ATTENTION = 'Nothing needs attention';

/** Nothing-to-do is claimed only when the headline and every line are known and zero. */
export function nothingNeedsAttention(headline: Figure, lines: readonly ExceptionLine[]): boolean {
  if (headline.state !== 'known' || headline.value !== 0) return false;
  return lines.every((l) => l.count === 0);
}

// ── Freshness stamp ─────────────────────────────────────────────────────────

/**
 * "Palletways, updated 10:31" (today), "Palletways, updated 19 Oct, 22:15"
 * (an earlier day) or "Palletways, update time unknown". The time is the
 * source's last successful update from the feed records, never the time this
 * page loaded.
 */
export function freshnessStamp(
  row: Pick<FreshnessRow, 'sources' | 'lastRefreshedAt'> | null | undefined,
  now: number = Date.now(),
): string {
  const names = (row?.sources ?? []).map((s) => s.name).filter(Boolean);
  const source = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0] ?? '';
  const last = row?.lastRefreshedAt ?? null;
  const parts = last ? ukClockParts(last) : null;
  if (!last || !parts) return source ? `${source}, update time unknown` : 'Update time unknown';
  const today = ukClockParts(new Date(now));
  const sameDay = !!today && today.year === parts.year && today.month === parts.month && today.day === parts.day;
  const when = sameDay ? formatUkTime(last) : formatUkDateTime(last);
  return source ? `${source}, updated ${when}` : `Updated ${when}`;
}

// ── Orders ──────────────────────────────────────────────────────────────────

/** Collections build through the day; the note is shown until this UK time. */
export const COLLECTIONS_ARRIVE_UNTIL = '16:00';

export interface OrdersLane {
  id: 'deliveries' | 'collections' | 'etas';
  title: string;
  headline: Figure;
  /** What the headline is counting, e.g. "delivered of 120 due". */
  headlineText: string;
  note?: string;
  exception?: ExceptionLine;
  /** Open work from earlier days that is still not done. */
  overdue?: ExceptionLine;
  target: CardTarget;
}

/**
 * A delivery still open that has had a failed attempt, or whose proof of
 * delivery was recorded and then reopened by a later scan. A reopened POD is
 * not necessarily a return, so the line says so in its wording. Uses `open`,
 * the rule the server itself uses for "not completed, needs attention"
 * (getOrderVolumeByDay.notCompletedAttention).
 */
export function isFailedOrReturned(row: OrdersDailyRow): boolean {
  return row.direction === 'delivery' && row.open === true
    && ((row.failed_attempts ?? 0) > 0 || row.ever_delivered === true);
}

const wall19 = (v: string | undefined): string | null => {
  const t = v?.trim().replace(' ', 'T').slice(0, 19);
  return t && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(t) ? t : null;
};

/**
 * A delivery not yet made whose live ETA has already passed: someone can still
 * act on it. (The "On-time vs ETA" figure only judges deliveries already made,
 * so it cannot say this.) Needs the carrier's live estimate; a delivery with no
 * estimate cannot be judged and is not counted. Compares UK wall clocks.
 */
export function isOutsideEtaNow(row: OrdersDailyRow, nowWall: string): boolean {
  if (row.direction !== 'delivery' || row.open !== true) return false;
  const end = row.eta_basis === 'point'
    ? wall19(row.eta_at) ?? wall19(row.eta_window_end)
    : wall19(row.eta_window_end) ?? wall19(row.eta_at);
  return end != null && end < nowWall.slice(0, end.length);
}

/**
 * The three Orders lanes, from the same summary the Orders tab gauges use
 * (deliveries, collections, "On-time vs ETA"). Lanes are side by side and
 * equal: no lane is the headline of the card.
 */
export function buildOrdersLanes(input: {
  summary: OrdersDailySummary | null;
  rows: readonly OrdersDailyRow[];
  loading: boolean;
  /** Now; read on the UK clock for the collections note and the live ETA check. */
  now: Date;
}): OrdersLane[] {
  const { summary, rows, loading, now } = input;
  const p = ukClockParts(now);
  const nowHm = p ? `${p.hour}:${p.minute}` : '00:00';
  const nowWall = p ? `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}` : '';
  const none: Figure = loading ? LOADING : UNKNOWN;

  // Open legs from earlier days are set apart from today's figures (the summary's
  // `overdue` block, filled when the read asks for the backlog). Not there = Unknown.
  const overdueLine = (id: string, text: string, metric: { consignments: number } | undefined, target: CardTarget): ExceptionLine => ({
    id,
    text,
    count: summary && metric ? metric.consignments : null,
    target,
    tone: summary && metric ? 'attention' : 'unknown',
  });

  const del = summary?.deliveries;
  const failed = summary ? rows.filter(isFailedOrReturned).length : null;
  const deliveries: OrdersLane = {
    id: 'deliveries',
    title: 'Deliveries',
    headline: del ? known(del.completed.consignments) : none,
    headlineText: del ? `delivered of ${del.total.consignments.toLocaleString('en-GB')} due today` : 'delivered',
    exception: {
      id: 'orders-failed-or-returned',
      text: 'Failed attempt or POD reopened',
      count: failed,
      target: CARD_LINKS.ordersDeliveries,
      tone: failed == null ? 'unknown' : 'attention',
    },
    overdue: overdueLine('orders-deliveries-overdue', 'Overdue from earlier days', summary?.overdue?.deliveries, CARD_LINKS.ordersDeliveries),
    target: CARD_LINKS.ordersDeliveries,
  };

  const col = summary?.collections;
  const collections: OrdersLane = {
    id: 'collections',
    title: 'Collections',
    headline: col ? known(col.total.consignments) : none,
    headlineText: 'booked so far today',
    note: nowHm < COLLECTIONS_ARRIVE_UNTIL ? `Still arriving until ${COLLECTIONS_ARRIVE_UNTIL}` : undefined,
    // A low count today is not a failure, so it is no exception. Collections
    // still open from earlier days are: someone has to chase them.
    overdue: overdueLine('orders-collections-overdue', 'Overdue from earlier days', summary?.overdue?.collections, CARD_LINKS.ordersCollections),
    target: CARD_LINKS.ordersCollections,
  };

  const eta = summary?.eta ?? null;
  const etaPct = eta?.completion_pct ?? null;
  const outsideEta = summary ? rows.filter((r) => isOutsideEtaNow(r, nowWall)).length : null;
  const etas: OrdersLane = {
    id: 'etas',
    title: 'ETAs',
    // Known only when at least one delivery could be judged against its ETA.
    headline: etaPct != null ? known(etaPct) : none,
    headlineText: 'on time against ETA',
    exception: {
      id: 'orders-outside-eta',
      text: 'Outside ETA now',
      // Open deliveries past their live ETA, not deliveries already made late.
      count: outsideEta,
      // eta_late exists as an Orders table filter but is not reachable through
      // highlightKPI, so this opens Deliveries.
      target: CARD_LINKS.ordersDeliveries,
      tone: outsideEta == null ? 'unknown' : 'attention',
    },
    target: CARD_LINKS.ordersDeliveries,
  };

  return [deliveries, collections, etas];
}

/** Percentage text for a lane headline; Unknown stays Unknown. */
export function formatLanePct(f: Figure): string {
  return f.state === 'known' ? formatPct(f.value) : f.state === 'loading' ? '…' : 'Unknown';
}

// ── Drivers: walkaround checks ──────────────────────────────────────────────

/**
 * WHEN A WALKAROUND CHECK COUNTS AS MISSING.
 * `start_of_shift`: a driver who has clocked on (shift started) with no matching
 * check. This is the rule zeecore applies when it marks a row `missing`
 * (driverWalkaroundChecks.ts); the page reads that verdict, it does not decide
 * its own. Kept as a named setting so a later rule (for example "no check an
 * hour into the shift") has one place to change. Not shown in the UI.
 */
export type WalkaroundMissingRule = 'start_of_shift';
export const WALKAROUND_MISSING_RULE: WalkaroundMissingRule = 'start_of_shift';

/** Short-check threshold used when the response does not say (zeecore MIN_WALKAROUND_MINUTES). */
export const DEFAULT_SHORT_CHECK_MINUTES = 15;

export interface WalkaroundRowLike {
  driver_key?: string;
  driver_name: string;
  status: string;
  short_check?: boolean;
}

export interface WalkaroundResponseLike {
  kpis: {
    missing: number;
    unverified?: number;
    checked?: number;
    before_drive?: number;
    after_drive?: number;
    short_check?: number;
    short_threshold_minutes?: number;
    unknown_duration?: number;
  };
  rows: WalkaroundRowLike[];
}

/** Rows that count as missing under the current rule. Unverified clock-ons are not drivers and are never counted. */
export function missingWalkaroundRows<T extends WalkaroundRowLike>(
  rows: readonly T[],
  rule: WalkaroundMissingRule = WALKAROUND_MISSING_RULE,
): T[] {
  switch (rule) {
    case 'start_of_shift':
    default:
      return rows.filter((r) => r.status === 'missing');
  }
}

/** How many names to list before "and N more". */
const MAX_NAMES = 6;

export interface DriversCardModel {
  headline: Figure;
  /** Display names of drivers with a missing check, in the order given. */
  missingNames: string[];
  moreNames: number;
  shortChecks: ExceptionLine;
  /** Clock-ons that could not be confirmed as drivers (not counted). */
  notConfirmed: number;
  nothingNeedsAttention: boolean;
}

export function buildDriversModel(input: {
  checks: WalkaroundResponseLike | null;
  loading: boolean;
}): DriversCardModel {
  const { checks, loading } = input;
  if (!checks) {
    const f = loading ? LOADING : UNKNOWN;
    return {
      headline: f,
      missingNames: [],
      moreNames: 0,
      shortChecks: {
        id: 'drivers-short-checks',
        text: `Walkaround checks under ${DEFAULT_SHORT_CHECK_MINUTES} minutes`,
        count: null,
        target: CARD_LINKS.drivers,
        tone: 'unknown',
      },
      notConfirmed: 0,
      nothingNeedsAttention: false,
    };
  }
  const missing = missingWalkaroundRows(checks.rows);
  const names = [...new Set(missing.map((r) => formatDriverDisplayName(r.driver_name) || r.driver_key || '').filter(Boolean))];
  const threshold = checks.kpis.short_threshold_minutes ?? DEFAULT_SHORT_CHECK_MINUTES;
  const shortCount = typeof checks.kpis.short_check === 'number'
    ? checks.kpis.short_check
    : checks.rows.filter((r) => r.short_check && (r.status === 'checked' || r.status === 'before_drive' || r.status === 'after_drive')).length;
  const headline = known(typeof checks.kpis.missing === 'number' ? checks.kpis.missing : missing.length);
  const shortChecks: ExceptionLine = {
    id: 'drivers-short-checks',
    text: `Walkaround checks under ${threshold} minutes`,
    count: shortCount,
    target: CARD_LINKS.drivers,
    tone: 'attention',
  };
  return {
    headline,
    missingNames: names.slice(0, MAX_NAMES),
    moreNames: Math.max(0, names.length - MAX_NAMES),
    shortChecks,
    notConfirmed: checks.kpis.unverified ?? 0,
    nothingNeedsAttention: nothingNeedsAttention(headline, [shortChecks]),
  };
}

// ── Compliance (same rules as the Driver tab for a single day) ──────────────

export interface ComplianceSummary {
  driverCount: number;
  /** Every violation in the period; marking one resolved records a review, it still counts. */
  violations: number;
  compliant: number;
  evaluated: number;
  notEvaluated: number;
  nonCompliant: number;
  atRisk: number;
  totalDrivingHours: number;
  totalWorkHours: number;
  totalRestHours: number;
  totalUnknownHours: number;
  totalShiftHours: number;
  hasActivityTimeSplit: boolean;
  openShiftCount: number;
  /** Today's hours are still building: drivers on shift, nothing to split yet. */
  hoursPendingForToday: boolean;
}

const finite = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * One day of drivers/summary, classified exactly as the Driver tab does for
 * Today (DriverCompliance filteredData): an open violation lifts a driver to
 * at-risk (or non-compliant at a risk score of 70+); a driver the hours rules
 * could not check stays "not evaluated" and is never counted as compliant.
 */
export function summariseCompliance(data: AquariusData): ComplianceSummary {
  const details: DriverDetail[] = data.driver_details ?? [];
  let violations = 0;
  let compliant = 0;
  let notEvaluated = 0;
  let atRisk = 0;
  let nonCompliant = 0;
  let driving = 0;
  let work = 0;
  let rest = 0;
  let unknownH = 0;
  let shift = 0;

  for (const d of details) {
    const active = d.violations ?? [];
    const drivingH = Number(d.driving_hours || 0);
    const restH = Number(d.rest_hours || 0);
    const shiftH = Number(d.shift_hours || 0);
    driving += drivingH;
    rest += restH;
    work += Number(d.work_hours || 0);
    unknownH += Number(d.unknown_hours || 0);
    shift += shiftH;

    let status: DriverDetail['compliance_status'] =
      d.compliance_status === 'compliant' || d.compliance_status === 'non-compliant'
      || d.compliance_status === 'at-risk' || d.compliance_status === 'unknown'
        ? d.compliance_status : 'unknown';
    const next = applyViolationsToStatus(status, finite(d.risk_score), active);
    status = next.status;
    violations += active.length;
    if (status === 'unknown') notEvaluated += 1;
    else if (status === 'compliant' && active.length === 0) compliant += 1;
    if (status === 'at-risk') atRisk += 1;
    if (status === 'non-compliant') nonCompliant += 1;
  }

  const driverCount = details.length;
  const evaluated = Math.max(0, driverCount - notEvaluated);
  const hasActivityTimeSplit = driving > 0 || rest > 0 || work > 0 || unknownH > 0;
  const openShiftCount = data.open_shift_count ?? 0;
  return {
    driverCount,
    violations,
    compliant,
    evaluated,
    notEvaluated,
    nonCompliant,
    atRisk,
    totalDrivingHours: driving,
    totalWorkHours: work,
    totalRestHours: rest,
    totalUnknownHours: unknownH,
    totalShiftHours: shift,
    hasActivityTimeSplit,
    openShiftCount,
    hoursPendingForToday: !hasActivityTimeSplit && shift <= 0 && openShiftCount > 0,
  };
}

/** Chart data, same slices and colours as the Driver tab's two charts. */
export function complianceChartData(s: ComplianceSummary) {
  const compliance = [
    { label: 'Compliant', value: s.compliant, color: '#22c55e' },
    { label: 'Non-Compliant', value: Math.max(0, s.evaluated - s.compliant), color: '#ef4444' },
  ];
  const time = s.hasActivityTimeSplit
    ? [
        { label: 'Driving Time', value: s.totalDrivingHours, color: '#22c55e' },
        { label: 'Work Time', value: s.totalWorkHours, color: '#f59e0b' },
        { label: 'Break Time', value: s.totalRestHours, color: '#3b82f6' },
        { label: 'Unknown', value: s.totalUnknownHours, color: '#8b5cf6' },
      ]
    : s.totalShiftHours > 0
      ? [{ label: 'Shift Hours (T&A)', value: s.totalShiftHours, color: '#6366f1' }]
      : [
          { label: 'Driving Time', value: 0, color: '#22c55e' },
          { label: 'Work Time', value: 0, color: '#f59e0b' },
          { label: 'Break Time', value: 0, color: '#3b82f6' },
          { label: 'Unknown', value: 0, color: '#8b5cf6' },
        ];
  return { compliance, time };
}

export interface ComplianceCardModel {
  headline: Figure;
  summary: ComplianceSummary | null;
  /** "X of Y drivers evaluated, N not evaluated" — always shown when known. */
  evaluatedCaption: string;
  lines: ExceptionLine[];
  nothingNeedsAttention: boolean;
}

export function buildComplianceModel(input: {
  data: AquariusData | null;
  loading: boolean;
}): ComplianceCardModel {
  const { data, loading } = input;
  if (!data) {
    return {
      headline: loading ? LOADING : UNKNOWN,
      summary: null,
      evaluatedCaption: '',
      lines: [],
      nothingNeedsAttention: false,
    };
  }
  const s = summariseCompliance(data);
  const lines: ExceptionLine[] = [
    {
      id: 'compliance-at-risk',
      text: 'Drivers at risk or non-compliant',
      count: s.atRisk + s.nonCompliant,
      target: CARD_LINKS.complianceAtRisk,
      tone: 'attention',
    },
    {
      id: 'compliance-not-evaluated',
      text: 'Drivers not evaluated',
      count: s.notEvaluated,
      target: CARD_LINKS.complianceAllDrivers,
      tone: 'attention',
    },
  ];
  const headline = known(s.violations);
  const evaluatedCaption = s.driverCount === 0
    ? 'No drivers in yesterday’s data'
    : `${s.evaluated} of ${s.driverCount} drivers evaluated, ${s.notEvaluated} not evaluated`;
  return {
    headline,
    summary: s,
    evaluatedCaption,
    lines,
    nothingNeedsAttention: nothingNeedsAttention(headline, lines),
  };
}

// ── Maintenance ─────────────────────────────────────────────────────────────

/**
 * MAJOR vs MINOR OPEN DEFECT (to be confirmed with the client).
 * Built only from fields the defect record already has:
 *   Major = the vehicle is off road (`vor_state` true) OR `severity` is
 *           `critical` or `major`.
 *   Other = every other open defect (`minor`, `advisory`, or no severity). It is
 *           labelled "Other", not "Minor": zeecore sets severity to `minor` when
 *           Aquarius sends none, so an unrated defect must not read as a judged one.
 *
 * There is no safety-critical flag on the defect
 * record today, so none is used.
 */
export const MAJOR_DEFECT_SEVERITIES: ReadonlySet<string> = new Set(['critical', 'major']);

type DefectLike = MaintenanceSummaryData['defects'][number];

/** Open = not resolved and not fixed, placeholders dropped (same as the Maintenance tab). */
export function openDefects(defects: readonly DefectLike[]): DefectLike[] {
  return defects.filter((d) => !isPlaceholderOutstandingDefectRow(d) && !d.resolved && String(d.status || '').toLowerCase() !== 'fixed');
}

export function isMajorDefect(d: Pick<DefectLike, 'severity' | 'vor_state'>): boolean {
  const vor = (d.vor_state as unknown) === true || String(d.vor_state).toLowerCase() === 'true';
  return vor || MAJOR_DEFECT_SEVERITIES.has(String(d.severity || '').trim().toLowerCase());
}

export interface NextBookedGroup {
  /** YYYY-MM-DD */
  date: string;
  regs: string[];
}

/**
 * The next `limit` distinct dates with planned work, from today onward, with
 * the registrations booked on each. Completed work and obligations Aquarius has
 * stopped vouching for (`stale`) are left out. Dates are dates: no times.
 */
export function nextBooked(
  schedule: MaintenanceScheduleData | null,
  todayYmd: string,
  limit = 3,
): NextBookedGroup[] | null {
  if (!schedule) return null;
  const byDate = new Map<string, Set<string>>();
  for (const o of schedule.obligations ?? []) {
    if (o.status === 'completed' || o.stale) continue;
    const day = String(o.due_date || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day) || day < todayYmd) continue;
    const regs = byDate.get(day) ?? new Set<string>();
    const reg = o.vehicle_registration?.trim();
    if (reg) regs.add(reg);
    byDate.set(day, regs);
  }
  return [...byDate.keys()].sort().slice(0, limit).map((date) => ({ date, regs: [...byDate.get(date)!].sort() }));
}

/** "Next booked: 24 Oct, AB12 CDE, FG34 HIJ; 26 Oct, KL56 MNO" or "None booked". Null while Unknown. */
export function formatNextBooked(groups: NextBookedGroup[] | null): string | null {
  if (groups == null) return null;
  if (groups.length === 0) return 'None booked';
  const parts = groups.map((g) => [formatUkDate(`${g.date}T12:00:00`), ...g.regs].join(', '));
  return `Next booked: ${parts.join('; ')}`;
}

export interface MaintenanceCardModel extends CardModel {
  id: 'maintenance';
  nextBooked: string | null;
}

export function buildMaintenanceModel(input: {
  summary: MaintenanceSummaryData | null;
  schedule: MaintenanceScheduleData | null;
  todayYmd: string;
  loading: boolean;
}): MaintenanceCardModel {
  const { summary, schedule, todayYmd, loading } = input;
  const none: Figure = loading ? LOADING : UNKNOWN;
  const open = summary ? openDefects(summary.defects ?? []) : null;
  const major = open ? open.filter(isMajorDefect).length : null;
  const minor = open && major != null ? open.length - major : null;
  const headline: Figure = summary && typeof summary.vor_asset_count === 'number' ? known(summary.vor_asset_count) : none;
  const lines: ExceptionLine[] = [
    {
      id: 'maintenance-major-defects',
      text: 'Major open defects',
      count: major,
      target: CARD_LINKS.maintenanceDefects,
      tone: major == null ? 'unknown' : 'attention',
    },
    {
      id: 'maintenance-other-defects',
      text: 'Other open defects',
      count: minor,
      target: CARD_LINKS.maintenanceDefects,
      tone: minor == null ? 'unknown' : 'attention',
    },
  ];
  return {
    id: 'maintenance',
    headline,
    headlineLabel: 'Vehicles off road',
    headlineTarget: CARD_LINKS.maintenanceVor,
    lines,
    nothingNeedsAttention: nothingNeedsAttention(headline, lines),
    nextBooked: formatNextBooked(nextBooked(schedule, todayYmd)),
  };
}

// ── Telematics ──────────────────────────────────────────────────────────────

export interface TelematicsTotalsLike {
  offlineVehicles?: number;
  prolongedIdleVehicles?: number;
  totalVehicles?: number;
}

/**
 * Offline now and idling 15 minutes or more. The data has a single "offline"
 * status; it does not separate "no tracker" from "offline", so the card says
 * offline. Totals are Unknown until the telematics feed has loaded.
 */
export function buildTelematicsModel(totals: TelematicsTotalsLike | null | undefined): CardModel {
  // No vehicles reporting at all means nothing was measured: Unknown, not zero.
  const measured = !!totals && totals.totalVehicles !== 0;
  const offline = measured && typeof totals!.offlineVehicles === 'number' ? totals!.offlineVehicles : null;
  const idle = measured && typeof totals!.prolongedIdleVehicles === 'number' ? totals!.prolongedIdleVehicles : null;
  const headline: Figure = offline == null ? UNKNOWN : known(offline);
  const lines: ExceptionLine[] = [
    {
      id: 'telematics-idling',
      text: 'Idling 15 min or more',
      count: idle,
      target: CARD_LINKS.telematicsIdle,
      tone: idle == null ? 'unknown' : 'attention',
    },
  ];
  return {
    id: 'telematics',
    headline,
    headlineLabel: 'Vehicles offline now',
    headlineTarget: CARD_LINKS.telematics,
    lines,
    nothingNeedsAttention: nothingNeedsAttention(headline, lines),
  };
}

/** The Orders card says "Nothing needs attention" only when every exception is known and zero. */
export function ordersNothingNeedsAttention(lanes: readonly OrdersLane[]): boolean {
  const exceptions = lanes.flatMap((l) => [l.exception, l.overdue]).filter((e): e is ExceptionLine => !!e);
  return exceptions.length > 0
    && exceptions.every((e) => e.count === 0)
    && lanes.every((l) => l.headline.state === 'known');
}
